/**
 * Pack Builder Override — collection merge, jump links, filters.
 * Loaded after pack-builder.js
 */

(function () {
    'use strict';

    function waitForPackBuilder(callback, maxAttempts = 50) {
        if (typeof PackBuilder !== 'undefined') {
            callback();
            return;
        }
        if (maxAttempts <= 0) {
            console.error('PackBuilder class not found after waiting. Load pack-builder.js first.');
            return;
        }
        setTimeout(() => waitForPackBuilder(callback, maxAttempts - 1), 50);
    }

    waitForPackBuilder(function () {
        // Row order on the builder and the pack editor. Christy's October 2026
        // category list: Gardencups, Pastacups, Graincups, Soupcups, Snackcups,
        // Treatcups. Producecups keeps its slot after Snackcups. A row only renders
        // when its collection is a bundle source with live products.
        const ALLOWED_COLLECTIONS = ['Gardencups', 'Pastacups', 'Graincups', 'Soupcups', 'Snackcups', 'Producecups', 'Treatcups'];
        const SALAD_SECTIONS = [
            'Gardencups',
            'Powercups w/Protein',
            'Gardencups (veggie)',
            'Powercups (veggie)',
        ];
        // Rows keep their -cups names (Josh, 22 Sep 2026); Produce is the one
        // legacy rename still in place.
        const COLLECTION_RENAME = {
            Salads: 'Gardencups',
        };
        const CATEGORY_FILTER_LABELS = {
            gardencups: 'Gardencups',
            pastacups: 'Pastacups',
            graincups: 'Graincups',
            soupcups: 'Soupcups',
            snackcups: 'Snackcups',
            producecups: 'Producecups',
            treatcups: 'Treatcups',
        };
        const ALLOWED_CATEGORY_VALUES = new Set(Object.keys(CATEGORY_FILTER_LABELS));

        function anchorId(label) {
            return `pb-section-${label.toLowerCase().replace(/\s+/g, '-')}`;
        }

        function txt(el) {
            return el && el.textContent ? el.textContent.trim() : '';
        }

        function getCollections() {
            return Array.from(document.querySelectorAll('.pb__collection'));
        }

        function getTitleEl(c) {
            return c && c.querySelector('.pb__collection-title');
        }

        function getCollectionByTitle(title) {
            return getCollections().find((c) => txt(getTitleEl(c)) === title);
        }

        function mergeSalads() {
            const main = getCollectionByTitle('Gardencups');
            if (!main) return;

            const list = main.querySelector('.pb__collection-products');
            if (!list) return;

            main.classList.add('merged-collection');

            const h = getTitleEl(main);
            if (h) h.textContent = 'Gardencups';

            SALAD_SECTIONS.forEach((name) => {
                if (name === 'Gardencups') return;

                const sec = getCollectionByTitle(name);
                if (!sec) return;

                sec.querySelectorAll('.pb__product-container').forEach((p) => {
                    list.appendChild(p);
                });
                sec.remove();
            });

            floatNewToFront(list);
        }

        // New cups first (Josh, 2 Oct 2026): once the salad collections are merged
        // into the one Gardencups row, any card carrying the NEW sticker moves to
        // the front, keeping its relative order. The sticker follows the badge-new
        // tag, so the row re-sorts itself at each launch with no code change.
        function floatNewToFront(list) {
            const cards = Array.from(list.querySelectorAll(':scope > .pb__product-container'));
            const fresh = cards.filter((c) => c.querySelector('.pb__product-new-badge'));
            if (!fresh.length) return;
            fresh.reverse().forEach((c) => {
                c.querySelectorAll('img[loading="lazy"]').forEach((img) => {
                    img.loading = 'eager';
                });
                list.prepend(c);
            });
        }

        function fixHeaders() {
            getCollections().forEach((c) => {
                const h = getTitleEl(c);
                const t = txt(h);
                if (COLLECTION_RENAME[t]) h.textContent = COLLECTION_RENAME[t];
            });
        }

        function ensureSectionAnchors() {
            getCollections().forEach((c) => {
                const label = txt(getTitleEl(c));
                if (!ALLOWED_COLLECTIONS.includes(label)) return;
                c.id = anchorId(label);
            });
        }

        function reorderSections() {
            const wrap = document.querySelector('.pb__collections');
            if (!wrap) return;

            const map = {};
            getCollections().forEach((c) => {
                const label = txt(getTitleEl(c));
                if (ALLOWED_COLLECTIONS.includes(label) && !map[label]) map[label] = c;
            });

            ALLOWED_COLLECTIONS.forEach((label) => {
                const el = map[label];
                if (el) wrap.appendChild(el);
            });
        }

        function fixJumpLinks() {
            const wrap = document.querySelector('.pb__jump-links');
            if (!wrap) return;

            const byLabel = {};
            let newArrivalsLink = wrap.querySelector(
                '[data-pb-new-arrivals-jump="true"], a[href="#category-subscriber-new-products"]'
            );

            wrap.querySelectorAll('a').forEach((a) => {
                if (
                    a === newArrivalsLink ||
                    a.getAttribute('data-pb-new-arrivals-jump') === 'true' ||
                    a.getAttribute('data-target') === 'subscriber-new-products'
                ) {
                    newArrivalsLink = a;
                    return;
                }

                const raw = txt(a);
                const label = SALAD_SECTIONS.includes(raw) ? 'Gardencups' : COLLECTION_RENAME[raw] || raw;
                if (!ALLOWED_COLLECTIONS.includes(label)) return;

                a.textContent = label;
                a.setAttribute('href', `#${anchorId(label)}`);
                a.dataset.pbJump = label;
                a.removeAttribute('data-pb-new-arrivals-jump');

                if (!byLabel[label]) byLabel[label] = a;
            });

            wrap.innerHTML = '';

            const appendJumpLink = (anchor) => {
                const li = document.createElement('li');
                // Clear any pre-rebuild inline hide so visibility can be re-evaluated.
                anchor.style.display = '';
                li.appendChild(anchor);
                wrap.appendChild(li);
            };

            if (isNewArrivalsShowing()) {
                if (!newArrivalsLink) {
                    newArrivalsLink = document.createElement('a');
                }
                newArrivalsLink.href = '#category-subscriber-new-products';
                newArrivalsLink.className = 'pb__jump-link';
                newArrivalsLink.id = newArrivalsLink.id || 'jump-link-new-arrivals';
                newArrivalsLink.setAttribute('data-pb-new-arrivals-jump', 'true');
                newArrivalsLink.setAttribute('data-target', 'subscriber-new-products');
                newArrivalsLink.dataset.pbJump = 'New Arrivals';
                newArrivalsLink.textContent = 'New Arrivals';
                appendJumpLink(newArrivalsLink);
            }

            ALLOWED_COLLECTIONS.forEach((label) => {
                if (byLabel[label]) appendJumpLink(byLabel[label]);
            });

            wrap.querySelectorAll('a[data-pb-jump]').forEach((a) => {
                if (a.dataset.pbJumpBound === '1') return;
                a.dataset.pbJumpBound = '1';

                a.addEventListener('click', (e) => {
                    let id;
                    if (a.getAttribute('data-pb-new-arrivals-jump') === 'true') {
                        id = 'category-subscriber-new-products';
                    } else {
                        id = anchorId(a.dataset.pbJump);
                    }
                    const target = document.getElementById(id);
                    if (!target) return;

                    e.preventDefault();
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    history.replaceState(null, '', `#${id}`);
                });
            });

            wrap.classList.remove('hidden');
        }

        function isNewArrivalsShowing() {
            return !!document.getElementById('category-subscriber-new-products');
        }

        function fixCategoryFilters() {
            const cat = document.querySelector('.pb__filter.category');
            if (!cat) return;

            const list = cat.querySelector('.pb__filter-list');
            if (!list) return;

            const nodes = [];
            list.querySelectorAll('li').forEach((li) => {
                const input = li.querySelector('input[data-type="category"]');
                if (!input) return;

                const val = (input.getAttribute('data-value') || '').trim();
                if (!ALLOWED_CATEGORY_VALUES.has(val)) {
                    li.remove();
                    return;
                }

                const label = CATEGORY_FILTER_LABELS[val];
                const firstText = li.querySelector('label')?.childNodes?.[0];
                if (firstText) firstText.nodeValue = label;

                nodes.push({ li, label });
            });

            const map = {};
            nodes.forEach((x) => {
                if (!map[x.label]) map[x.label] = x.li;
            });

            list.innerHTML = '';
            ALLOWED_COLLECTIONS.forEach((label) => {
                if (map[label]) list.appendChild(map[label]);
            });
        }

        function removeFilterHeaders() {
            document
                .querySelectorAll('.pb__filter-head, .pb__filter.diet .pb__filter-title')
                .forEach((e) => e.remove());
        }

        function setCategoryInProduct() {
            document.querySelectorAll('[data-collection-shopify-id]').forEach((e) => {
                const category = e.closest('[data-category-filter]')?.getAttribute('data-category-filter')?.toLowerCase();
                if (category) e.setAttribute('data-filter-category', category);
            });

            document.querySelectorAll('[data-tags]').forEach((e) => {
                const t = e.getAttribute('data-tags');
                if (t) e.setAttribute('data-tags', t.toLowerCase());
            });
        }

        function syncCategoryFiltersWithJumpLinks() {
            const categoryList = document.querySelector('.pb__filter.category .pb__filter-list');
            if (!categoryList) return;

            const allowedTargets = new Set();
            document.querySelectorAll('.pb__jump-links [data-target]').forEach((link) => {
                const item = link.closest('li');
                if (link.style.display === 'none' || (item && item.style.display === 'none')) return;
                const target = (link.getAttribute('data-target') || '').trim().toLowerCase();
                if (target) allowedTargets.add(target);
            });

            categoryList.querySelectorAll('li').forEach((li) => {
                const input = li.querySelector('input[data-type="category"]');
                if (!input) return;

                const value = (input.getAttribute('data-value') || '').trim().toLowerCase();
                li.style.display = allowedTargets.has(value) ? '' : 'none';
            });
        }

        function resetFilter() {
            document.querySelectorAll('.pb__product-container > [data-product-id]').forEach((product) => {
                const container = product.closest('.pb__product-container');
                if (container) container.style.display = 'block';
            });
            document.querySelectorAll('[data-category-filter]').forEach((e) => {
                e.style.display = 'block';
            });
        }

        function overrideFilterFunction() {
            const filterButton = document.querySelector('.pb__filters-apply');
            if (!filterButton) return;

            filterButton.addEventListener(
                'click',
                (e) => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                },
                true
            );

            filterButton.addEventListener('pointerdown', () => {
                const selectedCategory = document.querySelectorAll('[data-type="category"]:checked');
                const selectedDiet = document.querySelectorAll('[data-type="diet"]:checked');
                const matchedProducts = new Set();

                if (selectedCategory.length || selectedDiet.length) {
                    selectedCategory.forEach((input) => {
                        const category = input.getAttribute('data-value')?.toLowerCase();
                        if (!category) return;
                        document
                            .querySelectorAll(`[data-filter-category*="${category}"]`)
                            .forEach((product) => matchedProducts.add(product));
                    });

                    selectedDiet.forEach((input) => {
                        const diet = input.getAttribute('data-value')?.toLowerCase();
                        if (!diet) return;
                        document.querySelectorAll(`[data-tags*="${diet}"]`).forEach((product) => {
                            const productId = product.querySelector('[data-product-id]');
                            if (productId) matchedProducts.add(productId);
                        });
                    });

                    document.querySelectorAll('.pb__product-container > [data-product-id]').forEach((product) => {
                        const container = product.closest('.pb__product-container');
                        if (container) container.style.display = 'none';
                    });

                    matchedProducts.forEach((product) => {
                        const container = product.closest('.pb__product-container');
                        if (container) container.style.display = 'block';
                    });

                    document.querySelectorAll('[data-category-filter]').forEach((e) => {
                        e.style.display = 'none';
                        e.querySelectorAll('.pb__collection-products .pb__product-container').forEach((productContainer) => {
                            if (productContainer.style.display !== 'none') {
                                e.style.display = 'block';
                            }
                        });
                    });
                } else {
                    resetFilter();
                }
                document.querySelector('.pb__drawer-close')?.click();
            });
        }

        function overrideClearFunction() {
            const clearButton = document.querySelector('.pb__filters-clear');
            if (!clearButton) return;

            clearButton.addEventListener('pointerdown', () => {
                resetFilter();
                const filterCount = document.querySelector('.pb__filters-count');
                if (filterCount) filterCount.textContent = 0;
                document.querySelector('.pb__drawer-close')?.click();
            });
        }

        function fixFilterCount() {
            document.querySelectorAll('[data-type="category"],[data-type="diet"]').forEach((e) => {
                e.addEventListener('click', () => {
                    const totalSelected = document.querySelectorAll(
                        '[data-type="category"]:checked,[data-type="diet"]:checked'
                    ).length;
                    const filterCount = document.querySelector('.pb__filters-count');
                    if (filterCount) filterCount.textContent = totalSelected;
                });
            });
        }

        function applyCollectionManagement() {
            setTimeout(() => {
                setCategoryInProduct();
                mergeSalads();
                fixHeaders();
                reorderSections();
                ensureSectionAnchors();
                fixJumpLinks();

                const pb = window.packBuilderInstance;
                if (pb) {
                    pb.jumpLinks = document.querySelectorAll('.pb__jump-link');
                    if (typeof pb.hideJumpLinksWithoutCategories === 'function') {
                        pb.hideJumpLinksWithoutCategories();
                    }
                }

                syncCategoryFiltersWithJumpLinks();
            }, 100);
        }

        Object.assign(PackBuilder.prototype, {
            allowedCollections: ALLOWED_COLLECTIONS,
            saladSections: SALAD_SECTIONS,
            collectionRename: COLLECTION_RENAME,

            anchorId,
            txt,
            getCollections,
            getTitleEl,
            getCollectionByTitle,

            mergeSalads,
            fixHeaders,
            ensureSectionAnchors,
            reorderSections,
            fixJumpLinks,
            fixCategoryFilters,
            removeFilterHeaders,
            setCategoryInProduct,
            syncCategoryFiltersWithJumpLinks,
            resetFilter,
            overrideFilterFunction,
            overrideClearFunction,
            fixFilterCount,
            applyCollectionManagement,
        });
    });
})();
